import { compactFlatten } from '@art-suite/art-core-ts';
import { ScanGroupOptimized, ScanLineItemOptimized, ScanOptionOptimized, ScanPartOptimized, ScanRootOptimized, ScanTaskOptimized } from '../ScanRootOptimized';
import { getTotalCost } from './StandardScanGetters';

/**
 * Checks if a scan part has any values set (setValue or calculatedValue).
 * This is used to determine if a task should appear on a scope report even if it has no cost.
 */
export const scanPartHasValues = (scanPart: ScanPartOptimized | null | undefined): boolean => {
  if (!scanPart) return false;
  for (const key in scanPart.values) {
    const value = scanPart.values[key];
    if (value.setValue != null || value.calculatedValue != null) {
      return true;
    }
  }
  return false;
};

/**
 * A scan part is "scoped" when it contributes to the project's cost.
 * - It is NOT marked as `notApplicable`
 * - AND it has a non-zero total cost (materialCost + laborCost)
 */
export const isScanPartScoped = (scanPart: ScanPartOptimized | undefined | null): boolean =>
  scanPart != null && !scanPart.notApplicable && Math.abs(getTotalCost(scanPart)) >= 0.01;

/**
 * @deprecated use isScanPartScoped instead
 */
export const getScanPartIsScoped = (scanPart: ScanPartOptimized | undefined | null): boolean => isScanPartScoped(scanPart);

export const isLineItemScoped = (lineItem: ScanLineItemOptimized | undefined | null): boolean => isScanPartScoped(lineItem);

export const isOptionScoped = (option: ScanOptionOptimized | undefined | null): boolean => isScanPartScoped(option);

export const isTaskScoped = (task: ScanTaskOptimized | undefined | null): boolean => isScanPartScoped(task);

export const isGroupScoped = (group: ScanGroupOptimized | undefined | null): boolean => isScanPartScoped(group);

export const isTaskPresent = (task: ScanTaskOptimized | undefined | null): boolean => !!task && (task.userAdded || isTaskShownOnScopeReport(task));

/**
 * Whether anything was photographed here. `allPhotoIds` is a rollup, so a group counts its tasks'
 * photos as well as its own.
 */
export const scanPartHasPhotos = (scanPart: ScanPartOptimized | undefined | null): boolean => (scanPart?.optimized?.allPhotoIds?.length || 0) > 0;

/**
 * In addition to normally scoped tasks, we also want to include documentation tasks in the scope report.
 * A task shows on the scope report if it is scoped, has any values set, OR carries photos — someone
 * having pointed a camera at it is as good a statement that it is in play as a cost or a measurement.
 *
 * `notApplicable` still settles it either way: it is the explicit "not part of this job", so it outranks
 * every reason to show the task.
 */
export const isTaskShownOnScopeReport = (task: ScanTaskOptimized | undefined | null): boolean =>
  !task?.notApplicable && (isTaskScoped(task) || scanPartHasValues(task) || scanPartHasPhotos(task));

export const getTasksOnScopeReport = (scanRoot: ScanRootOptimized): ScanTaskOptimized[] => {
  const scopedGroups = scanRoot?.groups?.filter(isGroupShownOnScopeReport) ?? [];
  return compactFlatten(scopedGroups.map(group => group.tasks?.filter(isTaskShownOnScopeReport)));
};

/**
 * A group shows on the scope report if it is scoped, has any task that shows on the scope report, OR
 * carries photos of its own.
 */
export const isGroupShownOnScopeReport = (group: ScanGroupOptimized | undefined | null): boolean =>
  !group?.notApplicable && (scanPartHasPhotos(group) || isGroupScoped(group) || !!group?.tasks?.some(task => isTaskShownOnScopeReport(task)));

export const isScanRootScoped = (scanRoot: ScanRootOptimized): boolean => !!scanRoot.groups?.some(group => isGroupScoped(group));

export const isScanRootShownOnScopeReport = (scanRoot: ScanRootOptimized): boolean => !!scanRoot.groups?.some(group => isGroupShownOnScopeReport(group));

export const hasGroupsScopedAfter = (scanRoot: ScanRootOptimized | undefined | null, afterTimestamp: number): boolean => {
  if (!scanRoot?.groups) return false;
  return scanRoot.groups.some(group => isGroupScoped(group) && group.updatedAt != null && group.updatedAt > afterTimestamp);
};

export const getAllScopedGroups = (scanRoot: ScanRootOptimized): ScanGroupOptimized[] => scanRoot.groups?.filter(group => isGroupScoped(group)) ?? [];

export const getAllScopedTasks = (scanRoot: ScanRootOptimized): ScanTaskOptimized[] =>
  getAllScopedGroups(scanRoot).flatMap(group => group.tasks?.filter(isTaskScoped));

export const getAllShownOnScopeReportGroups = (scanRoot: ScanRootOptimized): ScanGroupOptimized[] =>
  scanRoot.groups?.filter(group => isGroupShownOnScopeReport(group)) ?? [];

export const getAllShownOnScopeReportTasks = (scanRoot: ScanRootOptimized): ScanTaskOptimized[] =>
  getAllShownOnScopeReportGroups(scanRoot).flatMap(group => group.tasks?.filter(isTaskShownOnScopeReport)) ?? [];

export const getAllScopedOptions = (scanRoot: ScanRootOptimized): ScanOptionOptimized[] =>
  getAllScopedTasks(scanRoot).flatMap(task => task.options?.filter(isOptionScoped));

export const getAllScopedLineItems = (scanRoot: ScanRootOptimized): ScanLineItemOptimized[] =>
  getAllScopedOptions(scanRoot).flatMap(option => option.lineItems?.filter(isLineItemScoped));

/**
 * A scoped task's line items, leaving out any in an option, or on a line, marked not applicable: the work a task puts
 * in the scope. None for an unscoped task.
 */
export const getTaskLineItemsInScope = (task: ScanTaskOptimized | undefined): ScanLineItemOptimized[] =>
  task && isTaskScoped(task)
    ? (task.options ?? []).filter(option => !option.notApplicable).flatMap(option => (option.lineItems ?? []).filter(lineItem => !lineItem.notApplicable))
    : [];
